import { get, isNil } from 'lodash';
import { ExportDocumentSection } from '../export-task.interfaces';
import { ExportPropertyTypeTransformParams } from '../processors/types/processor-types.interface';
import { ExportFileFormat } from '../export-task.enum';

interface CSVPage {
  title: string;
  values: any[][];
  columnCount: number;
}

export interface ParsedCSVDocument {
  title: string;
  content: string;
}

/** @internal Do not use directly */
export class CSVGenerator {
  private pages: CSVPage[] = [];

  constructor(private sections: ExportDocumentSection[]) { }

  private getSectionHeaders(section: ExportDocumentSection) {
    const firstItem = section.list[0];
    // An empty export has no row to read the column order from, and reading keys off `undefined`
    // throws. Fall back to the localization map, then map as usual so the headers stay localized.
    const headerKeys = firstItem
      ? Object.keys(firstItem)
      : section.itemLocalization
        ? Object.keys(section.itemLocalization)
        : [];

    return headerKeys.map((key) => {
      if (typeof section.itemLocalization[key] === 'string') {
        return section.itemLocalization[key];
      }
      return key;
    });
  }

  private transformObjectProperties(target: any) {
    Object.entries(target).forEach(([key, value]) => {
      if (typeof value === 'object') {
        if (value && (value as any).__for_processor) {
          const tparams = value as ExportPropertyTypeTransformParams;
          if (tparams.formats.includes(ExportFileFormat.CSV)) {
            target[key] = tparams.transform() || tparams.fallback;
          } else {
            target[key] = tparams.fallback;
          }
        }
      }
    });
    return target;
  }

  private generatePages() {
    this.pages = this.sections.map((section) => {
      const page = {
        title: get(section.options, 'ignoreTitleOnOutput', false) ? '' : section.title,
        values: [
          // Headers
          this.getSectionHeaders(section),
          // Rows with values
          ...section.list.map((item) => Object.values(this.transformObjectProperties(item)).map((s) => {
            if (typeof s === 'string') {
              return s.replace(/\r\n/g, ' // ').replace(/\n/g, ' // ').replace(/,/g, ';');
            }
            return s;
          })),
        ],
        columnCount: 0
      }
      page.values.forEach((group) => {
        if (group.length > page.columnCount) {
          page.columnCount = group.length;
        }
      });

      return page;
    });
  }

  private transformPage(page: CSVPage) {
    return {
      title: page.title.replace(/ /g, '_').replace(/_-_/g, '_'),
      content: page.values.map((group) => `${group.join(',')}`).join('\n'),
    } as ParsedCSVDocument;
  }

  generateDocuments() {
    this.generatePages();
    return this.pages.map((page) => this.transformPage(page));
  }

  generateHorizontalDocument() {
    this.generatePages();

    let maxRows = 0;
    this.pages.forEach((page) => {
      if (page.values.length > maxRows) {
        maxRows = page.values.length;
      }
    });

    const singlePage: CSVPage = {
      title: this.pages[0].title,
      values: new Array(maxRows).fill(null).map(() => []),
      columnCount: 0,
    };

    for (let p = 0; p < this.pages.length; p++) {
      const page = this.pages[p];
      const divider = ` |||||  ${page.title}  ||||| `;
      for (let r = 0; r < singlePage.values.length; r++) {
        const row = singlePage.values[r];
        const values = page.values[r];
        if (values) {
          if (p > 0) row.push(divider);
          row.push(...values);
        } else {
          row.push(...new Array(page.columnCount + (p === 0 ? 0 : 1)).fill(''));
        }
      }
    }

    return this.transformPage(singlePage);
  }
}
